import { createContext, ReactNode, useState, useMemo, useContext } from 'react';
import {
  PRESENT_ZOOM_STORAGE,
  PresentDrawer,
  PresentDrawerItem,
} from './activity-builder-present.constants';
import { localStorageWrapper } from 'approot/shared/storage';
import { useGetInteractiveLessonParams } from '../activity-builder.hooks';
import { LESSON_ACTIVITIES } from 'global/constants';

const defaultMenuDrawer = {
  isActive: true,
  isDetailActive: false,
};

type Student = {
  id: number;
  classId: number;
  email: string;
};

type ActiveComponent = {
  id: string;
  itemId?: string;
};

type ActivityBuilder = {
  isZoomToFit: boolean;
  isStudentNamesVisible: boolean;
  setIsStudentNamesVisible: (state: boolean) => void;
  student: Student | null;
  setStudent: (id: Student | null) => void;
  setZoomToFit: (state: boolean) => void;
  initialiseZoomToFit: () => void;
  drawer: PresentDrawer;
  drawerType: PresentDrawerItem;
  activeComponent?: ActiveComponent;
  setActiveComponent: (
    component: ActiveComponent | undefined,
    metaData?: { itemId?: string }
  ) => void;
  setDrawer: (drawer: { isActive: boolean; type: PresentDrawerItem }) => void;
  classId: number | null;
  setClassId: (id: number | null) => void;
};

export const ActivityBuilderContext = createContext<ActivityBuilder>({
  isZoomToFit: true,
  isStudentNamesVisible: true,
  setIsStudentNamesVisible: () => {},
  drawer: defaultMenuDrawer,
  drawerType: PresentDrawerItem.None,
  student: null,
  setZoomToFit: () => {},
  initialiseZoomToFit: () => {},
  setDrawer: () => {},
  setStudent: () => {},
  activeComponent: undefined,
  setActiveComponent: () => {},
  classId: null,
  setClassId: () => {},
});

export const ActivityBuilderPresentContextProvider = ({
  initialDrawer,
  initialClassId,
  children,
}: {
  initialDrawer?: PresentDrawer;
  initialClassId: number | null;
  children: ReactNode;
}) => {
  const [isZoomToFit, setZoomToFit] = useState<boolean>(
    localStorageWrapper.getItem(PRESENT_ZOOM_STORAGE) === 'true'
  );
  const [isStudentNamesVisible, setIsStudentNamesVisible] =
    useState<boolean>(false);
  const [drawer, setDrawer] = useState<PresentDrawer>({
    ...defaultMenuDrawer,
    ...initialDrawer,
  });
  const [drawerType, setDrawerType] = useState<PresentDrawerItem>(
    PresentDrawerItem.None
  );

  const [student, setStudent] = useState<Student | null>(null);

  const [classId, setClassId] = useState<number | null>(initialClassId);

  const [activeComponent, setActiveComponent] = useState<
    ActiveComponent | undefined
  >(undefined);

  const value = useMemo(
    () => ({
      isZoomToFit,
      initialiseZoomToFit: () => {
        const value = localStorageWrapper.getItem(PRESENT_ZOOM_STORAGE);
        if (value === null) {
          setZoomToFit(true);
          localStorageWrapper.setItem(PRESENT_ZOOM_STORAGE, 'true');
        }
      },
      setZoomToFit: (state: boolean) => {
        localStorageWrapper.setItem(
          PRESENT_ZOOM_STORAGE,
          state ? 'true' : 'false'
        );
        setZoomToFit(state);
      },
      drawer,
      drawerType,
      isStudentNamesVisible,
      setIsStudentNamesVisible,
      setDrawer: (props: { isActive: boolean; type: PresentDrawerItem }) => {
        setDrawer({
          isActive: props.isActive,
          isDetailActive: props.type !== PresentDrawerItem.None,
        });
        setDrawerType(props.type);
      },
      student,
      setStudent,
      classId,
      setClassId,
      activeComponent,
      setActiveComponent: (
        component: ActiveComponent | undefined,
        metaData?: { itemId?: string }
      ) => {
        if (!component) {
          setActiveComponent(undefined);
          return;
        }
        setActiveComponent({
          id: component.id,
          itemId: metaData?.itemId,
        });
      },
    }),
    [
      isZoomToFit,
      setZoomToFit,
      drawer,
      drawerType,
      setDrawer,
      student,
      setStudent,
      classId,
      setClassId,
      isStudentNamesVisible,
      setIsStudentNamesVisible,
      activeComponent,
      setActiveComponent,
    ]
  );

  return (
    <ActivityBuilderContext.Provider value={value}>
      {children}
    </ActivityBuilderContext.Provider>
  );
};

export const useActivityBuilderPresentContext = () => {
  const c = useContext(ActivityBuilderContext);
  const { version, userClassId, contentId, contentType, isPublic, lang } =
    useGetInteractiveLessonParams();
  return {
    ...c,
    version,
    contentId,
    contentType,
    isLesson: contentType === LESSON_ACTIVITIES,
    isPublished: !!version,
    isPublic,
    userClassId,
    lang,
  };
};
